import {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  buildChartData,
  usageChartUtils,
  useCallSuccess,
  useJobTelemetryApiCall,
  useTelemetryDiscovery,
  useTenancyRegistryCall,
} from '@jarvis/react-portal-addons';
import { getEndDate, getStartDate } from '../../utils/usageData';

const { SQCM_TO_SQFT, SQCM_TO_SQM } = usageChartUtils;
const {
  buildFaxChartData,
  buildPrintAreaChartData,
  buildPrintPagesChartData,
  buildScanChartData,
  getNumMonthsAverage,
  ViewType,
} = buildChartData;

const useCalls = ({
  authProvider,
  orgSelector,
  stack,
  store,
  params,
  isSquareFeet,
  numMonths,
  numMonthsAverage,
  selectedYear,
  currentDate,
  createdOrgDate,
  deviceIdList,
  setAreaChartData,
  setPrintChartData,
  setScanChartData,
  setFaxChartData,
  setNumMonthsAverage,
  setCreatedOrgDate,
  shouldUseTelemetryDiscovery,
}) => {
  const [printSuccessDone, setPrintSuccessDone] = useState(false);
  const [printAreaSuccessDone, setPrintAreaSuccessDone] = useState(false);
  const [scanSuccessDone, setScanSuccessDone] = useState(false);
  const [faxSuccessDone, setFaxSuccessDone] = useState(false);
  const [devicesJobDataList, setDevicesJobDataList] = useState([]);
  const [telemetryDiscoveryList, setTelemetryDiscoveryList] = useState([]);

  const jobTelemetryApiCall = useJobTelemetryApiCall({
    authProvider,
    stack,
    params,
  });

  const tenancyRegistryCall = useTenancyRegistryCall({
    authProvider,
    stack,
    id: orgSelector?.getOrgTenantId(),
  });

  const telemetryDiscoveryCall = useTelemetryDiscovery({ authProvider, stack, store });

  const isDiscoveryReady = useMemo(() => (
    telemetryDiscoveryCall.pending === false
    && (telemetryDiscoveryCall.success === null)
  ), [telemetryDiscoveryCall.pending, telemetryDiscoveryCall.success]);

  useEffect(() => {
    if (deviceIdList && deviceIdList.length > 0) {
      setDevicesJobDataList(deviceIdList.map(deviceId => ({
        deviceId,
      })));
    }
  }, [deviceIdList]);

  const onTenancyRegistrySuccess = useCallback(data => {
    const orgDate = new Date(data?.dateCreated * 1000);

    setCreatedOrgDate(orgDate);
  }, [
    setCreatedOrgDate,
  ]);

  useCallSuccess({
    call: tenancyRegistryCall,
    onSuccess: onTenancyRegistrySuccess,
  });

  useEffect(() => {
    if (createdOrgDate) {
      setNumMonthsAverage(getNumMonthsAverage({
        currentDate,
        selectedYear,
        createOrgDate: createdOrgDate,
      }));
    }
  }, [selectedYear, currentDate, createdOrgDate, setNumMonthsAverage]);

  const canRunTelemetryCalls = tenancyRegistryCall.success
    && !tenancyRegistryCall.pending
    && !!createdOrgDate
    && deviceIdList.length === devicesJobDataList.length
    && devicesJobDataList.length > 0;

  const {
    print: printCall,
    printArea: printAreaCall,
    scan: scanCall,
    fax: faxCall,
  } = jobTelemetryApiCall.data;

  useEffect(() => {
    if (printAreaSuccessDone && faxSuccessDone && scanSuccessDone && printSuccessDone) {
      const devicesThatNeedDiscovery = devicesJobDataList.map(device => ({
        ...device,
        needsTelemetry: !device.hasPrintData
          && !device.hasPrintAreaData
          && !device.hasScanData
          && !device.hasFaxData,
      })).filter(device => device.needsTelemetry).map(device => device.deviceId);

      setTelemetryDiscoveryList(devicesThatNeedDiscovery);
    }
  }, [
    deviceIdList,
    devicesJobDataList,
    faxSuccessDone,
    printAreaSuccessDone,
    printSuccessDone,
    scanSuccessDone,
  ]);

  useEffect(() => {
    if (isDiscoveryReady && telemetryDiscoveryList.length > 0 && shouldUseTelemetryDiscovery) {
      telemetryDiscoveryCall.makeApiCall({
        deviceIdList: telemetryDiscoveryList,
        eventCategory: 'lifetimeCounterSnapshot',
      });
    }
  }, [telemetryDiscoveryList, isDiscoveryReady, telemetryDiscoveryCall, shouldUseTelemetryDiscovery]);

  const onPrintAreaSuccess = useCallback(data => {
    const { usageByPrintCategory: devices } = data;
    devices.forEach(device => {
      const { deviceId } = device;
      setDevicesJobDataList(
        current => current.map(
          deviceData => (
            deviceData.deviceId === deviceId
              ? { ...deviceData, hasPrintAreaData: !!device.lastDataReceivedOn } : deviceData
          ),
        ),
      );
    });
    const unit = isSquareFeet ? SQCM_TO_SQFT : SQCM_TO_SQM;
    const areaData = buildPrintAreaChartData({
      payload: data,
      totalRange: numMonths,
      unit,
      rangeToAverage: numMonthsAverage,
      startDate: new Date(getStartDate(selectedYear)),
      endDate: new Date(getEndDate(selectedYear, currentDate)),
      viewType: ViewType.Monthly,
    });
    setPrintAreaSuccessDone(true);
    setAreaChartData(areaData);
  }, [
    isSquareFeet,
    numMonths,
    numMonthsAverage,
    selectedYear,
    currentDate,
    setAreaChartData,
  ]);

  useCallSuccess({
    init: canRunTelemetryCalls,
    call: printAreaCall,
    onSuccess: onPrintAreaSuccess,
  });

  const onPrintCallSuccess = useCallback(data => {
    const { printCounters: devices } = data;
    devices.forEach(device => {
      const { deviceId } = device;
      setDevicesJobDataList(
        current => current.map(
          deviceData => (
            deviceData.deviceId === deviceId
              ? { ...deviceData, hasPrintData: !!device.lastDataReceivedOn }
              : deviceData
          ),
        ),
      );
    });
    const printData = buildPrintPagesChartData({
      payload: data,
      totalRange: numMonths,
      rangeToAverage: numMonthsAverage,
      startDate: new Date(getStartDate(selectedYear)),
      endDate: new Date(getEndDate(selectedYear, currentDate)),
      viewType: ViewType.Monthly,
    });
    setPrintSuccessDone(true);
    setPrintChartData(printData);
  }, [
    numMonths,
    numMonthsAverage,
    selectedYear,
    currentDate,
    setPrintChartData,
  ]);

  useCallSuccess({
    init: canRunTelemetryCalls,
    call: printCall,
    onSuccess: onPrintCallSuccess,
  });

  const onScanCallSuccess = useCallback(data => {
    const { scanCounters: devices } = data;
    devices.forEach(device => {
      const { deviceId } = device;
      setDevicesJobDataList(
        current => current.map(
          deviceData => (
            deviceData.deviceId === deviceId
              ? { ...deviceData, hasScanData: !!device.lastDataReceivedOn }
              : deviceData
          ),
        ),
      );
    });
    const scanData = buildScanChartData({
      payload: data,
      totalRange: numMonths,
      rangeToAverage: numMonthsAverage,
      startDate: new Date(getStartDate(selectedYear)),
      endDate: new Date(getEndDate(selectedYear, currentDate)),
      viewType: ViewType.Monthly,
    });
    setScanSuccessDone(true);
    setScanChartData(scanData);
  }, [
    numMonths,
    numMonthsAverage,
    selectedYear,
    currentDate,
    setScanChartData,
  ]);

  useCallSuccess({
    init: canRunTelemetryCalls,
    call: scanCall,
    onSuccess: onScanCallSuccess,
  });

  const onFaxCallSuccess = useCallback(data => {
    const { faxCounters: devices } = data;
    devices.forEach(device => {
      const { deviceId } = device;
      setDevicesJobDataList(
        current => current.map(
          deviceData => (
            deviceData.deviceId === deviceId
              ? { ...deviceData, hasFaxData: !!device.lastDataReceivedOn }
              : deviceData
          ),
        ),
      );
    });
    const faxData = buildFaxChartData({
      payload: data,
      totalRange: numMonths,
      rangeToAverage: numMonthsAverage,
      startDate: new Date(getStartDate(selectedYear)),
      endDate: new Date(getEndDate(selectedYear, currentDate)),
      viewType: ViewType.Monthly,
    });
    setFaxSuccessDone(true);
    setFaxChartData(faxData);
  }, [
    numMonths,
    numMonthsAverage,
    selectedYear,
    currentDate,
    setFaxChartData,
  ]);

  useCallSuccess({
    init: canRunTelemetryCalls,
    call: faxCall,
    onSuccess: onFaxCallSuccess,
  });

  const allJobCalls = useMemo(() => ({
    tenancyRegistryCall,
    jobTelemetryApiCall,
  }), [tenancyRegistryCall, jobTelemetryApiCall]);

  return {
    printCall,
    printAreaCall,
    scanCall,
    faxCall,
    tenancyRegistryCall,
    allJobCalls,
  };
};

export default useCalls;
